import { useRef, useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { uploadFile } from "@/lib/api";
import { formatDuration } from "@/lib/format";
import { Paperclip, Mic, Send, Loader2, Trash2, Image as ImageIcon, FileText } from "lucide-react";

// Safari/iOS records AAC-in-MP4, not webm — pick the first container the
// browser actually supports so voice notes play back everywhere.
const RECORDER_MIMES = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"];
function pickRecorderMime() {
  if (typeof MediaRecorder === "undefined" || !MediaRecorder.isTypeSupported) return "";
  return RECORDER_MIMES.find((m) => MediaRecorder.isTypeSupported(m)) || "";
}

export function ChatComposer({ onSend, onTyping, accent = "#0e8f7e", disabled, variant = "default", surface = "light" }) {
  const [text, setText] = useState("");
  const [uploading, setUploading] = useState(false);
  const [recording, setRecording] = useState(false);
  const [recSeconds, setRecSeconds] = useState(0);
  const [showAttach, setShowAttach] = useState(false);
  const taRef = useRef(null);
  const fileRef = useRef(null);
  const photoRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const recTimerRef = useRef(null);
  // Duration must live in a ref: the recorder's onstop closure would otherwise
  // capture the stale recSeconds value from the render that started recording.
  const recSecondsRef = useRef(0);
  const typingTimeoutRef = useRef(null);
  const isWa = variant === "whatsapp";
  const dark = surface === "dark";

  useEffect(() => () => {
    clearInterval(recTimerRef.current);
    clearTimeout(typingTimeoutRef.current);
  }, []);

  useEffect(() => {
    const el = taRef.current;
    if (!el) return;
    // Empty → clear the inline height so it collapses to a single row (rows=1);
    // never leave a stale/tall height that shows a big empty box. Grow only as
    // the user types.
    if (!text) {
      el.style.height = "";
      return;
    }
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  }, [text]);

  const send = async () => {
    const value = text.trim();
    if (!value) return;
    setText("");
    const ok = await Promise.resolve(onSend({ content: value, message_type: "text" }));
    if (ok === false) {
      // Put the text back so nothing is lost on a failed request.
      setText((t) => t || value);
      toast.error("Message didn't send. Check your connection and try again.");
    }
  };

  const handleChange = (e) => {
    setText(e.target.value);
    if (onTyping) {
      onTyping(true);
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = setTimeout(() => onTyping(false), 1500);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const info = await uploadFile(file);
      const ok = await Promise.resolve(onSend({
        content: "",
        message_type: info.message_type,
        media_url: info.file_url,
        media_filename: info.media_filename,
        media_size: info.media_size,
        media_content_type: info.media_content_type,
      }));
      if (ok === false) toast.error("Couldn't send the file. Please try again.");
    } catch {
      toast.error("Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = pickRecorderMime();
      const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        clearInterval(recTimerRef.current);
        if (recorderRef.current?.cancelled) {
          setRecording(false);
          setRecSeconds(0);
          return;
        }
        const type = rec.mimeType || mime || "audio/webm";
        const ext = /mp4|aac/.test(type) ? "m4a" : "webm";
        const blob = new Blob(chunksRef.current, { type });
        const dur = recSecondsRef.current;
        setRecording(false);
        setRecSeconds(0);
        setUploading(true);
        try {
          const file = new File([blob], `voice-${Date.now()}.${ext}`, { type });
          const info = await uploadFile(file);
          const ok = await Promise.resolve(onSend({
            content: "",
            message_type: "voice",
            media_url: info.file_url,
            media_filename: info.media_filename,
            media_size: info.media_size,
            media_content_type: info.media_content_type,
            duration: dur,
          }));
          if (ok === false) toast.error("Couldn't send the voice note. Please try again.");
        } catch {
          toast.error("Could not send voice note.");
        } finally {
          setUploading(false);
        }
      };
      recorderRef.current = rec;
      rec.start();
      setRecording(true);
      setRecSeconds(0);
      recSecondsRef.current = 0;
      recTimerRef.current = setInterval(() => {
        recSecondsRef.current += 1;
        setRecSeconds(recSecondsRef.current);
      }, 1000);
    } catch {
      toast.error("Microphone access is required to record voice notes.");
    }
  };

  const stopRecording = (cancel = false) => {
    if (recorderRef.current) {
      recorderRef.current.cancelled = cancel;
      recorderRef.current.stop();
    }
  };

  if (recording) {
    return (
      <div className="flex items-center gap-3 px-3 py-2.5" data-testid="composer-recording">
        <button onClick={() => stopRecording(true)} className="flex h-11 w-11 items-center justify-center rounded-full text-red-500 hover:bg-red-500/10" data-testid="cancel-recording-button" aria-label="Cancel recording">
          <Trash2 className="h-5 w-5" aria-hidden="true" />
        </button>
        <div className={`flex flex-1 items-center gap-2 text-sm font-medium ${dark ? "text-gray-200" : "text-[#111b18]"}`}>
          <span className="h-2.5 w-2.5 animate-pulse rounded-full bg-red-500" />
          Recording… {formatDuration(recSeconds)}
        </div>
        <button onClick={() => stopRecording(false)} className="flex h-12 w-12 items-center justify-center rounded-full text-white shadow-md transition-transform hover:scale-105 active:scale-95" style={{ background: accent }} data-testid="send-recording-button" aria-label="Send voice note">
          <Send className="h-5 w-5" aria-hidden="true" />
        </button>
      </div>
    );
  }

  const iconColor = dark ? "text-gray-400 hover:text-[#2ac67b]" : "text-[#54656f] hover:text-[#0e8f7e]";
  const pillClass = dark ? "bg-white/5 border border-white/10" : "bg-white shadow-sm";

  // Two separate inputs (no `capture`, so no camera): Photos opens the gallery
  // image picker, Files opens the document browser.
  const HiddenInputs = (
    <>
      <input ref={photoRef} type="file" accept="image/*" hidden onChange={handleFile} data-testid="image-input" />
      <input ref={fileRef} type="file" hidden onChange={handleFile} data-testid="file-input" />
    </>
  );

  const pick = (ref) => {
    // Open the file dialog FIRST (must stay inside the user-gesture), then close
    // the menu — closing first can cancel the dialog in some mobile WebViews.
    ref.current?.click();
    setShowAttach(false);
  };

  const AttachMenu = (
    <AnimatePresence>
      {showAttach && (
        <>
          {/* tap-away backdrop */}
          <button
            className="fixed inset-0 z-20 cursor-default"
            onClick={() => setShowAttach(false)}
            aria-label="Close attachment menu"
            tabIndex={-1}
          />
          <motion.div
            initial={{ opacity: 0, y: 8, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.96 }}
            transition={{ type: "spring", stiffness: 400, damping: 30 }}
            className={`absolute bottom-[64px] left-2 z-30 w-52 origin-bottom-left overflow-hidden rounded-2xl p-1.5 shadow-2xl ${dark ? "glass" : "border border-black/5 bg-white"}`}
            data-testid="attach-menu"
          >
            <button
              onClick={() => pick(photoRef)}
              className={`flex w-full items-center gap-3 rounded-xl px-2.5 py-2.5 text-left transition-colors ${dark ? "hover:bg-white/10" : "hover:bg-black/[0.04]"}`}
              data-testid="attach-photos"
            >
              <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full" style={{ background: `${accent}1f`, color: accent }}>
                <ImageIcon className="h-[18px] w-[18px]" aria-hidden="true" />
              </span>
              <span className={`text-[15px] font-medium ${dark ? "text-gray-100" : "text-[#111b18]"}`}>Photos</span>
            </button>
            <button
              onClick={() => pick(fileRef)}
              className={`flex w-full items-center gap-3 rounded-xl px-2.5 py-2.5 text-left transition-colors ${dark ? "hover:bg-white/10" : "hover:bg-black/[0.04]"}`}
              data-testid="attach-files"
            >
              <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-[#5b6ff5]/15 text-[#5b6ff5]">
                <FileText className="h-[18px] w-[18px]" aria-hidden="true" />
              </span>
              <span className={`text-[15px] font-medium ${dark ? "text-gray-100" : "text-[#111b18]"}`}>Files</span>
            </button>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  const TextArea = (
    <textarea
      ref={taRef}
      value={text}
      onChange={handleChange}
      onKeyDown={onKeyDown}
      rows={1}
      placeholder={uploading ? "Uploading…" : isWa ? "Message" : "Type a message"}
      disabled={disabled}
      className={`sc-scroll min-w-0 flex-1 resize-none bg-transparent leading-snug outline-none ${isWa ? "py-1 text-[16px] placeholder:text-[#8a938f]" : `px-1 py-1.5 text-[15px] ${dark ? "text-white placeholder:text-gray-500" : "placeholder:text-[#8a938f]"}`}`}
      style={{ maxHeight: 120 }}
      data-testid="message-input"
    />
  );

  const ExternalButton = (
    <button
      onClick={text.trim() ? send : startRecording}
      disabled={disabled}
      className={`flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full shadow-md transition-transform hover:scale-105 active:scale-90 disabled:opacity-50 ${dark ? "text-black neon-glow" : "text-white"}`}
      style={{ background: accent }}
      data-testid={text.trim() ? "send-message-button" : "record-voice-button"}
      aria-label={text.trim() ? "Send message" : "Record voice note"}
    >
      {text.trim() ? <Send className="h-5 w-5" aria-hidden="true" /> : <Mic className="h-6 w-6" aria-hidden="true" />}
    </button>
  );

  if (isWa) {
    return (
      <div className="relative flex items-end gap-2 px-2 py-2">
        {HiddenInputs}
        {AttachMenu}
        <div className="flex min-w-0 flex-1 items-end gap-2.5 rounded-[24px] bg-white px-4 py-2 shadow-sm">
          {TextArea}
          {uploading && <Loader2 className="mb-1 h-5 w-5 flex-shrink-0 animate-spin text-[#0e8f7e]" aria-hidden="true" />}
          <button className={`flex-shrink-0 pb-1 transition-colors ${showAttach ? "text-[#0e8f7e]" : "text-[#7b847f] hover:text-[#0e8f7e]"}`} onClick={() => setShowAttach((s) => !s)} data-testid="attach-file-button" aria-label="Attach photo or file">
            <Paperclip className="h-[23px] w-[23px] -rotate-45" aria-hidden="true" />
          </button>
        </div>
        {ExternalButton}
      </div>
    );
  }

  return (
    <div className="relative flex items-end gap-2 px-2.5 py-2.5">
      {HiddenInputs}
      {AttachMenu}
      <div className={`flex min-w-0 flex-1 items-end gap-1.5 rounded-[22px] px-3 py-1.5 ${pillClass}`}>
        <button className={`flex-shrink-0 pb-1.5 transition-colors ${showAttach ? (dark ? "text-[#2ac67b]" : "text-[#0e8f7e]") : iconColor}`} onClick={() => setShowAttach((s) => !s)} data-testid="attach-file-button" aria-label="Attach photo or file">
          <Paperclip className="h-[22px] w-[22px]" aria-hidden="true" />
        </button>
        {TextArea}
        {uploading && <Loader2 className={`mb-1.5 h-5 w-5 flex-shrink-0 animate-spin ${dark ? "text-[#2ac67b]" : "text-[#0e8f7e]"}`} aria-hidden="true" />}
      </div>
      {ExternalButton}
    </div>
  );
}
